import { useEffect, useRef, useState } from 'react';
/**
 * Returns whether or not window is scrolled to the bottom considering a given threshold.
 */
export function useIsScrolledToBottom(
  bottomScrollThreshold = 0
): [boolean, React.Dispatch<React.SetStateAction<HTMLDivElement | null>>] {
  const [bottom, _top, setRef] = useIsScrolledToVerticalEdges(bottomScrollThreshold, 0);
  return [bottom, setRef];
}

/**
 * Returns whether or not window is scrolled to the top considering a given threshold.
 */
export function useIsScrolledToTop(
  topScrollThreshold = 0
): [boolean, React.Dispatch<React.SetStateAction<HTMLDivElement | null>>] {
  const [_bottom, top, setRef] = useIsScrolledToVerticalEdges(topScrollThreshold, 0);
  return [top, setRef];
}

/**
 * Returns whether or not window is scrolled to bottom or top while considering given thresholds.
 */
export function useIsScrolledToVerticalEdges(
  bottomScrollThreshold = 0,
  topScrollThreshold = 0
): [boolean, boolean, React.Dispatch<React.SetStateAction<HTMLDivElement | null>>] {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const isTicking = useRef(false);
  const [node, setRef] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!node && window.scrollY === 0) {
        // this was not a vertical scroll, so don't react
        setIsScrolledToTop(true);
        return;
      }

      // since the scroll event fires at a high rate, only handle
      // the event once per animation frame
      if (!isTicking.current) {
        window.requestAnimationFrame(() => {
          if (node) {
            setIsScrolledToBottom(
              node.scrollTop >= node.scrollHeight - node.offsetHeight - bottomScrollThreshold
            );
            setIsScrolledToTop(node.scrollTop <= topScrollThreshold);
          } else {
            setIsScrolledToBottom(
              window.scrollY + window.innerHeight >= document.body.scrollHeight - bottomScrollThreshold
            );
            setIsScrolledToTop(window.scrollY <= topScrollThreshold);
          }

          isTicking.current = false;
        });

        isTicking.current = true;
      }
    };
    if (node) {
      node.addEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (node) {
        node.removeEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [bottomScrollThreshold, topScrollThreshold, node]);

  return [isScrolledToBottom, isScrolledToTop, setRef];
}
